/**
 * <bundle-tshirt> Custom Element
 *
 * - Size selection & variant switching
 * - AJAX add-to-cart
 * - Image gallery thumbnails
 * - Pack & color switching via Section Rendering API (no page reload)
 * - Browser back/forward support
 */
class BundleTshirt extends HTMLElement {
  connectedCallback() {
    // These bind to `this` (the element) — only once, ever
    if (!this._globalsBound) {
      this._globalsBound = true;
      this.#bindNavigation();
      this.#bindBundlePurchaseGuard();
      window.addEventListener('popstate', () => this.#onPopState());
    }

    this.pack ||= window.BundlePack ? new window.BundlePack(this) : null;
    this.pack?.connect();
    this.#init();
  }

  disconnectedCallback() {
    this.pack?.disconnect();
    clearTimeout(this._kiwiReloadTimer);
    this._kiwiCache?.remove();
    this._kiwiCharts?.clear();
  }

  // ══════════════════════════════════════
  //  Pack layout hooks (assets/custom-bundle-pack.js)
  // ══════════════════════════════════════

  // For a pack script that loads after this element connected.
  attachPack() {
    if (this.pack || !window.BundlePack) return;
    this.pack = new window.BundlePack(this);
    this.pack.connect();
    if (this.product) this.pack.init();
  }

  currentVariant() {
    return this.#currentVariant();
  }

  setVariant(variant) {
    this.#setVariant(variant);
  }

  restoreButton() {
    this.#restoreButton();
  }

  syncNotifyButton(variant) {
    this.#syncNotifyButton(variant);
  }

  updateLabelForButton(btn) {
    this.#updateLabelForButton(btn);
  }

  setPerUnitPrice(cents, count) {
    this.#setPerUnitPrice(cents, count);
  }

  money(cents) {
    return this.#money(cents);
  }

  onCartSuccess() {
    return this.#onCartSuccess();
  }

  navigateTo(productUrl) {
    return this.#navigateTo(productUrl);
  }

  syncKiwiData() {
    this.#syncKiwiProductData();
  }

  reloadKiwi() {
    clearTimeout(this._kiwiReloadTimer);
    this.#reloadKiwiSizing();
  }

  kiwiCacheHost() {
    return this.#kiwiCacheHost();
  }

  #init(options = {}) {
    const refreshSizing = Boolean(this.product);
    clearTimeout(this._kiwiReloadTimer);
    this.pack?.reset();
    this.product = this.#parseProductJSON();
    if (!this.product) return;

    const cachedChart = this._kiwiCharts?.get(String(this.product.id));
    const chartPlaceholder = this.querySelector('#KiwiSizingChart');
    if (cachedChart && chartPlaceholder && cachedChart !== chartPlaceholder) {
      chartPlaceholder.replaceWith(cachedChart);
      cachedChart.id = 'KiwiSizingChart';
    }
    this.#syncKiwiProductData();

    this.elements = {
      form: this.querySelector('form'),
      variantSelect: this.querySelector('[name="id"]'),
      submitBtn: this.querySelector('.bundle-atc__button[type="submit"]'),
      price: this.querySelector('.bundle-price__current'),
      comparePrice: this.querySelector('.bundle-price__compare'),
      perUnitPrice: this.querySelector('.bundle-price__per-unit'),
      priceDisclosure: this.querySelector('.price-disclosure'),
      thumbs: this.querySelectorAll('.bundle-gallery__thumb'),
      mainImages: this.querySelectorAll('.bundle-gallery__main-image'),
    };

    // These bind to child elements (replaced on each swap) — safe to re-bind
    this.#bindSizes();

    this.#bindGallery();
    this.#bindForm();
    this.#bindPricingDisclosure();
    this.#bindReviewsBadge();
    this.#syncFromURL();

    this.pack?.init(options);
    if (refreshSizing) this._kiwiReloadTimer = setTimeout(() => this.#reloadKiwiSizing(), 0);
  }

  // ══════════════════════════════════════
  //  Product Data
  // ══════════════════════════════════════

  #parseProductJSON() {
    const el = this.querySelector(':scope > script[type="application/json"]');
    try {
      const product = JSON.parse(el?.textContent);
      this.#enrichVariantsWithInventory(product);
      return product;
    } catch {
      return null;
    }
  }

  #enrichVariantsWithInventory(product) {
    if (!product?.variants?.length) return;

    const select = this.querySelector('[name="id"]');
    if (!select) return;

    [...select.options].forEach((opt) => {
      const variant = product.variants.find((v) => String(v.id) === opt.value);
      if (!variant) return;

      const qty = parseInt(opt.dataset.inventoryQuantity, 10);
      variant.inventory_quantity = Number.isFinite(qty) ? qty : null;
      variant.inventory_management = opt.dataset.inventoryManagement || null;
    });
  }

  #syncKiwiProductData() {
    const chart = this.querySelector('#KiwiSizingChart');
    if (!chart) return;

    let options = this.product.options;
    try {
      options = JSON.parse(chart.dataset.productOptions) || options;
    } catch {}

    this.pack?.kiwiSource(chart);

    window.KiwiSizing = window.KiwiSizing || {};
    window.KiwiSizing.data = {
      collections: chart.dataset.collections,
      tags: chart.dataset.tags,
      product: chart.dataset.product,
      vendor: chart.dataset.vendor,
      type: chart.dataset.type,
      title: chart.dataset.productName,
      images: this.product.images,
      options,
      variants: this.product.variants,
    };
  }

  #kiwiCacheHost() {
    if (!this._kiwiCache?.isConnected) {
      this._kiwiCache = document.createElement('div');
      this._kiwiCache.hidden = true;
      this._kiwiCache.dataset.bundleKiwiCache = '';
      this.after(this._kiwiCache);
    }
    return this._kiwiCache;
  }

  #parkKiwiSizing() {
    const chart = this.querySelector('#KiwiSizingChart');
    if (!chart) return;
    this.#kiwiCacheHost();
    this._kiwiCharts ||= new Map();
    this._kiwiCharts.set(chart.dataset.ownProduct || chart.dataset.product, chart);
    chart.removeAttribute('id');
    // KIWI reinjection restores disconnected chart nodes; keep cached charts connected during swaps.
    this._kiwiCache.append(chart);
  }

  #reloadKiwiSizing(attempt = 0) {
    const chart = this.querySelector('#KiwiSizingChart');
    if (!this.isConnected || !chart || chart.classList.contains('kiwiSizingLoaded')) return;
    if (typeof window.ks?.loadSizing !== 'function') {
      if (attempt < 80) this._kiwiReloadTimer = setTimeout(() => this.#reloadKiwiSizing(attempt + 1), 250);
      return;
    }
    try {
      window.ks.loadSizing({ productData: window.KiwiSizing.data, options: {} });
    } catch (error) {
      console.warn('KIWI size chart reload failed', error);
    }
  }

  #getSelectedOptions() {
    const selected = {};
    this.querySelectorAll('.bundle-size-btn.is-active').forEach((btn) => {
      selected[btn.dataset.optionIndex] = btn.dataset.value;
    });
    return selected;
  }

  #findVariantBySelection() {
    const selected = this.#getSelectedOptions();
    if (!Object.keys(selected).length) return null;

    return this.product.variants.find((v) => {
      if (selected['1'] !== undefined && v.option1 !== selected['1']) return false;
      if (selected['2'] !== undefined && v.option2 !== selected['2']) return false;
      if (selected['3'] !== undefined && v.option3 !== selected['3']) return false;
      return true;
    });
  }

  #variantOption(variant, optionIndex) {
    if (optionIndex === '1' || optionIndex === 1) return variant.option1;
    if (optionIndex === '2' || optionIndex === 2) return variant.option2;
    if (optionIndex === '3' || optionIndex === 3) return variant.option3;
    return null;
  }

  #currentVariant() {
    const id = parseInt(this.elements.variantSelect?.value, 10);
    return this.product.variants.find((v) => v.id === id);
  }

  // ══════════════════════════════════════
  //  Pack & Color Navigation (no reload)
  // ══════════════════════════════════════

  #bindBundlePurchaseGuard() {
    this.addEventListener(
      'click',
      (event) => {
        const button = event.target.closest('.bundle-atc__button');
        if (!button) return;
        if (button.id === 'BIS_trigger') {
          event.preventDefault();
          event.stopImmediatePropagation();
          this.#openRestockSignup();
          return;
        }
        if (!this.#comboActive()) return;
        // Cart apps can intercept the button before a submit event reaches our form.
        event.preventDefault();
        event.stopImmediatePropagation();
        if (!button.disabled) this.elements.form?.requestSubmit(button);
      },
      { capture: true },
    );
  }

  #bindNavigation() {
    this.addEventListener('click', (e) => {
      const link = e.target.closest('.bundle-pack-card, .bundle-colors__item');
      if (!link || link.classList.contains('is-active')) return;

      const href = link.getAttribute('href');
      if (!href) return;

      e.preventDefault();
      this.#navigateTo(href);
    });
  }

  #comboActive() {
    return this.pack?.comboActive() ?? false;
  }

  async #navigateTo(productUrl) {
    const requestId = (this._navigationRequestId = (this._navigationRequestId || 0) + 1);
    this.classList.add('is-loading');

    // Remember selected size before swap
    const activeSize = this.querySelector('.bundle-size-btn.is-active')?.dataset.value;

    try {
      const sectionUrl = new URL(productUrl, location.origin);
      sectionUrl.searchParams.set('section_id', this.dataset.sectionId || 'product-template-bundle-tshirt');
      const res = await fetch(sectionUrl);
      if (!res.ok) throw new Error('Failed to fetch section');

      const html = await res.text();
      if (requestId !== this._navigationRequestId) return;
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const newElement = doc.querySelector('bundle-tshirt');

      if (!newElement) throw new Error('Section not found in response');

      // Swap content
      this.#parkKiwiSizing();
      this.innerHTML = newElement.innerHTML;

      // Copy data attributes
      for (const attr of newElement.attributes) {
        if (attr.name !== 'class') this.setAttribute(attr.name, attr.value);
      }

      this.pack?.onSwap(doc);
      this.#init({ url: productUrl });
      this.#reinitJudgeMe();

      // Restore size selection (or fall back to first available)
      this.#restoreSize(activeSize);

      const variant = this.#currentVariant();
      const url = new URL(productUrl, location.origin);
      url.searchParams.delete('subview');
      if (variant) url.searchParams.set('variant', variant.id);
      history.pushState({ bundleNav: true }, '', url.toString());
    } catch {
      if (requestId !== this._navigationRequestId) return;
      window.location.href = productUrl;
    } finally {
      if (requestId === this._navigationRequestId) this.classList.remove('is-loading');
    }
  }

  #restoreSize(sizeValue) {
    if (!this.product) return;

    // Try the previously selected size (option1) — find first available variant with it
    if (sizeValue) {
      const variant = this.product.variants.find((v) => v.option1 === sizeValue && v.available);
      if (variant) {
        this.#applyVariantToButtons(variant);
        this.#setVariant(variant);
        return;
      }
    }

    // Fall back to first available variant
    const firstAvailable = this.product.variants.find((v) => v.available);
    if (firstAvailable) {
      this.#applyVariantToButtons(firstAvailable);
      this.#setVariant(firstAvailable);
    }
  }

  #applyVariantToButtons(variant) {
    this.#activateButtonForOption(1, variant.option1);
    this.#activateButtonForOption(2, variant.option2);
    this.#activateButtonForOption(3, variant.option3);
    this.#updateOptionAvailability();
  }

  #onPopState() {
    // Re-fetch section for the current URL
    this.#navigateReplace(location.pathname + location.search);
  }

  async #navigateReplace(url) {
    const requestId = (this._navigationRequestId = (this._navigationRequestId || 0) + 1);
    this.classList.add('is-loading');
    try {
      const sectionUrl = new URL(url, location.origin);
      sectionUrl.searchParams.set('section_id', this.dataset.sectionId || 'product-template-bundle-tshirt');
      const res = await fetch(sectionUrl);
      if (!res.ok) throw new Error();

      const html = await res.text();
      if (requestId !== this._navigationRequestId) return;
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const newElement = doc.querySelector('bundle-tshirt');

      if (newElement) {
        this.#parkKiwiSizing();
        this.innerHTML = newElement.innerHTML;
        for (const attr of newElement.attributes) {
          if (attr.name !== 'class') this.setAttribute(attr.name, attr.value);
        }

        this.pack?.onSwap(doc);
        this.#init();
        this.#reinitJudgeMe();
      }
    } catch {
      if (requestId !== this._navigationRequestId) return;
      location.reload();
    } finally {
      if (requestId === this._navigationRequestId) this.classList.remove('is-loading');
    }
  }

  // ══════════════════════════════════════
  //  Size Selection
  // ══════════════════════════════════════

  #bindSizes() {
    this.querySelectorAll('.bundle-size-btn').forEach((btn) => {
      btn.addEventListener('click', () => {
        if (btn.disabled) return;

        const optionIndex = btn.dataset.optionIndex;

        // Deactivate only buttons in the SAME option group (size, inseam, etc.)
        this.querySelectorAll(`.bundle-size-btn[data-option-index="${optionIndex}"]`).forEach((b) =>
          b.classList.remove('is-active'),
        );
        btn.classList.add('is-active');

        // Update label inside the same option container
        this.#updateLabelForButton(btn);

        // Re-evaluate availability of buttons in OTHER option groups based on new selection
        this.#updateOptionAvailability();

        // Combine all selected options to find the matching variant
        const variant = this.#findVariantBySelection();
        if (variant) this.#setVariant(variant);
      });
    });
  }

  #updateLabelForButton(btn) {
    const container = btn.closest('.bundle-sizes');
    const labelEl = container?.querySelector('.bundle-sizes__selected');
    if (labelEl) labelEl.textContent = this.pack?.sizeLabel(btn) ?? btn.dataset.value;
  }

  #updateOptionAvailability() {
    const selected = this.#getSelectedOptions();

    this.querySelectorAll('.bundle-size-btn').forEach((btn) => {
      const optionIndex = btn.dataset.optionIndex;
      const value = btn.dataset.value;

      // Existing sold-out variants remain selectable for restock signups.
      // Only combinations that do not exist are disabled.
      const matching = this.product.variants.filter((v) => {
        if (this.#variantOption(v, optionIndex) !== value) return false;
        for (const [idx, val] of Object.entries(selected)) {
          if (idx === optionIndex) continue;
          if (this.#variantOption(v, idx) !== val) return false;
        }
        return true;
      });
      const isAvailable = matching.some((v) => v.available);

      btn.classList.toggle('bundle-size-btn--disabled', !isAvailable);
      btn.disabled = matching.length === 0;
      btn.setAttribute(
        'aria-label',
        `${value}${isAvailable ? '' : matching.length ? ' — Out of stock' : ' — Unavailable'}`,
      );
    });
  }

  #syncFromURL() {
    const variantId = new URLSearchParams(location.search).get('variant');

    if (variantId) {
      const variant = this.product.variants.find((v) => String(v.id) === variantId);
      if (variant) {
        this.#applyVariantToButtons(variant);
        this.#setVariant(variant);
        return;
      }
    }

    // No ?variant= in URL — Liquid pre-rendered active buttons via option.selected_value.
    // Update labels for each pre-active button and refresh cross-option availability.
    this.querySelectorAll('.bundle-size-btn.is-active').forEach((btn) => this.#updateLabelForButton(btn));
    this.#updateOptionAvailability();
    this.#renderButton(this.#currentVariant());
  }

  #activateButtonForOption(optionIndex, value) {
    if (value === undefined || value === null) return;
    this.querySelectorAll(`.bundle-size-btn[data-option-index="${optionIndex}"]`).forEach((b) =>
      b.classList.remove('is-active'),
    );
    const safeValue = typeof CSS !== 'undefined' && CSS.escape ? CSS.escape(value) : value;
    const target = this.querySelector(
      `.bundle-size-btn[data-option-index="${optionIndex}"][data-value="${safeValue}"]`,
    );
    if (target) {
      target.classList.add('is-active');
      this.#updateLabelForButton(target);
    }
  }

  // ══════════════════════════════════════
  //  Variant Switching
  // ══════════════════════════════════════

  #setVariant(variant) {
    const { variantSelect, price, comparePrice, priceDisclosure } = this.elements;
    if (!variantSelect) return;

    // Update <select>
    const id = String(variant.id);
    [...variantSelect.options].forEach((opt) => {
      opt.selected = opt.value === id;
    });
    variantSelect.value = id;

    if (this.#comboActive()) {
      this.pack.evaluateBundle();
      const url = new URL(location.href);
      url.searchParams.set('variant', variant.id);
      history.replaceState({}, '', url);
      if (variant.featured_image) this.#switchImage(String(variant.featured_image.id));
      this.#updateLowStock(variant);
      return;
    }

    // Price
    if (price) price.textContent = this.#money(variant.price);
    this.#setPerUnitPrice(variant.price, Number(this.dataset.packSize) || 1);
    const hasCompare = variant.compare_at_price > variant.price;
    if (comparePrice) {
      comparePrice.textContent = hasCompare ? this.#money(variant.compare_at_price) : '';
      comparePrice.style.display = hasCompare ? '' : 'none';
    }
    if (priceDisclosure) {
      priceDisclosure.classList.toggle('is-hidden', !hasCompare);
      if (!hasCompare) this.#closePricingDisclosure();
    }

    this.#renderButton(variant);

    // URL (replace, not push — size change is not a "page")
    const url = new URL(location.href);
    url.searchParams.set('variant', variant.id);
    history.replaceState({}, '', url);

    // Image
    if (variant.featured_image) this.#switchImage(String(variant.featured_image.id));

    this.#updateLowStock(variant);
  }

  #updateLowStock(variant) {
    const thresholdMsg = this.querySelector('[data-low-stock-threshold]');
    if (!thresholdMsg) return;

    const managed = Boolean(variant?.inventory_management);
    const qty = variant?.inventory_quantity;
    const show = managed && Number.isFinite(qty) && qty > 0 && qty < 10;

    thresholdMsg.classList.toggle('is-hidden', !show);
    if (show) {
      thresholdMsg.removeAttribute('hidden');
    } else {
      thresholdMsg.setAttribute('hidden', '');
    }
  }

  // ══════════════════════════════════════
  //  Gallery
  // ══════════════════════════════════════

  #bindGallery() {
    this.elements.thumbs.forEach((thumb) => {
      thumb.addEventListener('click', () => this.#switchImage(thumb.dataset.imageId));
    });
  }

  #switchImage(imageId) {
    this.elements.thumbs.forEach((t) => t.classList.toggle('is-active', t.dataset.imageId === imageId));
    this.elements.mainImages.forEach((img) => img.classList.toggle('is-active', img.dataset.imageId === imageId));
  }

  // ══════════════════════════════════════
  //  AJAX Cart
  // ══════════════════════════════════════

  #bindForm() {
    const submit = async (e) => {
      if (this.dataset.ajax !== 'true' && !this.#comboActive()) return;
      e.preventDefault();
      if (this.#comboActive() && this.pack.adding) return;

      const btn = this.elements.submitBtn;
      if (btn) {
        btn.disabled = true;
        btn.textContent = 'Adding...';
      }

      try {
        if (this.#comboActive()) {
          await this.pack.addBundleToCart();
        } else {
          const res = await fetch('/cart/add.js', {
            method: 'POST',
            body: new FormData(this.elements.form),
          });

          if (!res.ok) {
            const { description } = await res.json();
            throw new Error(description || 'Could not add to cart');
          }

          await this.#onCartSuccess();
        }
      } catch (err) {
        this.#notify('error', err.message);
      } finally {
        this.#restoreButton();
      }
    };
    this.elements.form?.addEventListener(
      'submit',
      (event) => {
        if (!this.#comboActive()) return;
        event.stopImmediatePropagation();
        submit(event);
      },
      { capture: true },
    );
    this.elements.form?.addEventListener('submit', (event) => {
      if (!this.#comboActive()) submit(event);
    });
  }

  // ══════════════════════════════════════
  //  Pricing Disclosure Modal
  // ══════════════════════════════════════

  #closePricingDisclosure(returnFocus = false) {
    const block = this.querySelector('.pricing-disclosure');
    const toggle = block?.querySelector('.disclosure-popup-toggle');
    const popup = block?.querySelector('.disclosure-popup');
    const overlay = block?.querySelector('.disclosure-overlay');
    if (!toggle || !popup || !overlay) return;

    popup.classList.remove('is-open');
    overlay.classList.remove('is-open');
    toggle.setAttribute('aria-expanded', 'false');
    document.body.classList.remove('disclosure-no-scroll');
    popup.hidden = true;
    if (returnFocus) toggle.focus();
  }

  #bindPricingDisclosure() {
    const block = this.querySelector('.pricing-disclosure');
    if (!block) return;

    const toggle = block.querySelector('.disclosure-popup-toggle');
    const popup = block.querySelector('.disclosure-popup');
    const overlay = block.querySelector('.disclosure-overlay');
    const closeBtn = block.querySelector('.close-button');
    if (!toggle || !popup || !overlay || !closeBtn) return;

    const openPopup = () => {
      popup.hidden = false;
      popup.classList.add('is-open');
      overlay.classList.add('is-open');
      toggle.setAttribute('aria-expanded', 'true');
      document.body.classList.add('disclosure-no-scroll');
      closeBtn.focus();
    };

    const closePopup = () => this.#closePricingDisclosure(true);

    toggle.addEventListener('click', () => {
      if (popup.classList.contains('is-open')) {
        closePopup();
      } else {
        openPopup();
      }
    });

    closeBtn.addEventListener('click', closePopup);
    overlay.addEventListener('click', closePopup);
    popup.addEventListener('click', (e) => {
      if (e.target === popup) closePopup();
    });
    popup.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') closePopup();
    });
  }

  async #onCartSuccess() {
    this.#notify('success');
    document.querySelector('.site-header__cart')?.classList.add('has-items');
    document.querySelector('.site-header__cart-indicator')?.classList.remove('hide');

    try {
      const cart = await fetch('/cart.js').then((r) => r.json());
      const countEl = document.querySelector('.site-header__cart-count');
      if (countEl) countEl.textContent = cart.item_count;

      const cartLink = document.querySelector('.site-header__cart');
      if (cartLink) {
        if (cart.item_count > 0) {
          cartLink.classList.add('has-items');
          document.querySelector('.site-header__cart-indicator')?.classList.remove('hide');
        } else {
          cartLink.classList.remove('has-items');
          document.querySelector('.site-header__cart-indicator')?.classList.add('hide');
        }
      }

      document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart } }));
      document.dispatchEvent(new CustomEvent('cart:refresh'));
      const drawer = window.CustomCartDrawer;
      if (drawer?.isActive()) {
        if (drawer.isOpen) drawer.refresh();
        else drawer.open();
      } else {
        if (typeof window.SLIDECART_UPDATE === 'function') window.SLIDECART_UPDATE();
        if (typeof window.SLIDECART_OPEN === 'function') window.SLIDECART_OPEN();
      }
    } catch {
      /* silent */
    }
  }

  #restoreButton() {
    if (this.#comboActive()) {
      this.pack.restoreButton();
      return;
    }

    this.#renderButton(this.#currentVariant());
  }

  #syncNotifyButton(variant) {
    const submitBtn = this.elements.submitBtn;
    if (!submitBtn) return;
    const soldOut = Boolean(variant && !variant.available && !this.#comboActive());
    let notifyBtn = this.querySelector('#BIS_trigger');
    if (soldOut && !notifyBtn) {
      notifyBtn = document.createElement('button');
      notifyBtn.id = 'BIS_trigger';
      notifyBtn.type = 'button';
      notifyBtn.className = 'bundle-atc__button';
      submitBtn.insertAdjacentElement('afterend', notifyBtn);
    }
    submitBtn.style.display = soldOut ? 'none' : '';
    if (notifyBtn) {
      notifyBtn.textContent = 'Notify me when available';
      notifyBtn.setAttribute('aria-label', 'Notify me when available');
      notifyBtn.dataset.variantId = variant ? String(variant.id) : '';
      notifyBtn.style.display = soldOut ? 'inline-block' : 'none';
    }
  }

  async #openRestockSignup() {
    const variant = this.#currentVariant();
    if (!variant || variant.available || this.#comboActive()) return;
    try {
      if (typeof window.BIS?.createPopover !== 'function')
        throw new Error('Restock notifications are still loading. Please try again.');
      // The app's global popup retains the original product after AJAX navigation.
      // Keep a product-specific popup and pass the exact selected sold-out variant.
      if (this._restockProduct !== this.product) {
        this._restockPopover = window.BIS.createPopover({
          product: this.product,
          productHandle: this.product.handle,
          button: { visible: false },
        });
        this._restockProduct = this.product;
      }
      const popover = this._restockPopover;
      await popover.ready;
      // BIS ready covers product data, not the iframe's asynchronous render.
      // selectVariant reads #variants.options, which is absent on a cold open.
      let variantField;
      for (let attempt = 0; attempt < 100; attempt++) {
        variantField = popover.form?.frame?.contentDocument?.querySelector('#variants');
        if (variantField) break;
        await new Promise((resolve) => setTimeout(resolve, 50));
      }
      if (!variantField) throw new Error('Restock notifications could not load. Please try again.');
      if (this.#currentVariant()?.id !== variant.id || this.#comboActive()) return;
      popover.form.show({ variantId: variant.id });
    } catch (error) {
      this.#notify('error', error.message);
    }
  }

  #renderButton(variant) {
    const btn = this.elements.submitBtn;
    if (!btn) return;
    this.#syncNotifyButton(variant);

    btn.disabled = !variant?.available;
    if (!variant?.available) {
      btn.textContent = 'Sold Out';
      return;
    }

    // Intelligems reprices .igPrice by its data attributes, so the rendered span is kept rather than rebuilt.
    let price = btn.querySelector('.igPrice');
    if (price?.dataset.variantId !== String(variant.id)) {
      price = document.createElement('span');
      price.className = 'igPrice';
      price.dataset.productId = this.product.id;
      price.dataset.variantId = variant.id;
      price.textContent = this.#money(variant.price);
    }
    btn.replaceChildren('Add to Cart — ', price);
  }

  // ══════════════════════════════════════
  //  Reviews Badge → Scroll to Reviews
  // ══════════════════════════════════════

  #bindReviewsBadge() {
    const badge = this.querySelector('.jdgm-preview-badge');
    const reviews = this.querySelector('#judgeme_product_reviews');
    if (!badge || !reviews) return;

    badge.style.cursor = 'pointer';
    badge.addEventListener('click', (e) => {
      e.preventDefault();
      reviews.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }

  // ══════════════════════════════════════
  //  Re-init Judge.me after AJAX swap
  // ══════════════════════════════════════

  #reinitJudgeMe() {
    if (typeof jdgm !== 'undefined' && jdgm.customizeBadges) {
      jdgm.customizeBadges();
    }
    if (typeof jdgm !== 'undefined' && jdgm.customizeWidgets) {
      jdgm.customizeWidgets();
    }
  }

  // ══════════════════════════════════════
  //  Helpers
  // ══════════════════════════════════════

  #notify(type, message = false) {
    if (typeof theme !== 'undefined' && theme.Notify) {
      theme.Notify.open(type, message, true);
    } else if (type === 'error' && message) {
      alert(message);
    }
  }

  #money(cents) {
    return theme.Currency.formatMoney(cents, theme.moneyFormat);
  }

  #setPerUnitPrice(cents, count) {
    const { perUnitPrice } = this.elements;
    const unit = this.dataset.unitLabel;
    if (perUnitPrice)
      perUnitPrice.textContent = count > 1 && unit ? `${this.#money(Math.floor(cents / count))} / ${unit}` : '';
  }
}

customElements.define('bundle-tshirt', BundleTshirt);
